export function resolveVideoWidget(items = [], { uuid, index, url } = {}) {
  if (uuid) return items.find((item) => item.regionUUID === uuid);
  if (index != null && index !== '') {
    const matches = items.filter(
      (item) => item.videoWidgetIndex != null && Number(item.videoWidgetIndex) === Number(index),
    );
    return matches.length === 1 ? matches[0] : undefined;
  }
  // Old HTML has only data-videourl. Identical legacy URLs remain indistinguishable.
  if (url) return items.find((item) => item.videoUrl === url);
}

export function videoId(video) {
  try {
    const url = new URL(video.videoUrl);
    if (!['https:', 'http:'].includes(url.protocol)) return null;
    const host = url.hostname.replace(/^www\./, '');
    if (
      Number(video.source) === 1 &&
      ['youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'youtu.be'].includes(host)
    ) {
      const id =
        host === 'youtu.be'
          ? url.pathname.slice(1)
          : url.searchParams.get('v') || url.pathname.split('/').pop();
      return /^[\w-]{11}$/.test(id || '') ? id : null;
    }
    if (Number(video.source) === 2 && /(^|\.)vimeo(?:pro)?\.com$/.test(host)) {
      return url.pathname.match(/\/(\d+)(?:\/|$)/)?.[1] || null;
    }
  } catch {}
  return null;
}

export function normalizeVideos(widget = {}, order = '') {
  const input = Array.isArray(widget.videos) && widget.videos.length ? widget.videos : [widget];
  const videos = input
    .filter((video) => video && videoId(video))
    .map((video) => ({
      ...video,
      source: Number(video.source),
      autoPlay: video.autoPlay === true,
      mute: video.mute === true,
      loop: video.loop === true,
      startTime: Math.max(0, Number(video.startTime) || 0),
    }));
  const ids = [
    ...new Set(
      String(order || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ];
  if (!ids.length || !Array.isArray(widget.videos) || !widget.videos.length) return videos;
  return ids.map((id) => videos.find((video) => video.videoUUID === id)).filter(Boolean);
}

export function resolveVideoMaxHeight(value, containerHeight) {
  const match = String(value ?? '')
    .trim()
    .match(/^(\d+(?:\.\d+)?)(px|%)?$/i);
  if (!match) return null;
  const amount = Number(match[1]);
  if (match[2] === '%') return containerHeight == null ? null : (containerHeight * amount) / 100;
  return amount;
}

export function videoLayout(width, limit, hasCarousel, slidesPerView, headerHeight = 0) {
  const spaceBetween = hasCarousel ? Math.min(12, width / (2 * slidesPerView)) : 0;
  const gap = hasCarousel ? Math.min(12, width / 16) : 0;
  const playerHeight = (width * 9) / 16;
  const rowHeight = hasCarousel
    ? (((width - spaceBetween * (slidesPerView - 1)) / slidesPerView) * 9) / 16
    : 0;
  const totalHeight = playerHeight + rowHeight + gap;
  const available = limit == null ? Infinity : Math.max(0, limit - (Number(headerHeight) || 0));
  const scale = totalHeight > 0 ? Math.min(1, available / totalHeight) : 1;
  // Scale the complete block, including spacing, so the player and thumbnail
  // row share both edges and every thumbnail retains its 16:9 shape.
  return {
    playerWidth: width * scale,
    playerHeight: playerHeight * scale,
    rowHeight: rowHeight * scale,
    gap: gap * scale,
    spaceBetween: spaceBetween * scale,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

// Date only, parsed as text so it never shifts by timezone. Accepts YYYY-MM-DD
// (any time part is ignored), M/D/YY and M/D/YYYY. Returns 'Mon D, YYYY' or ''.
export function formatVideoDate(value) {
  const text = trimmed(value);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  const us = iso ? null : text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!iso && !us) return '';
  const year = iso ? Number(iso[1]) : Number(us[3].length === 2 ? `20${us[3]}` : us[3]);
  const month = Number(iso ? iso[2] : us[1]);
  const day = Number(iso ? iso[3] : us[2]);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth) return '';
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

// The "Now playing" header for a video. The API's category / publishedDate win
// when either is present; otherwise a legacy Pseudo Title separated by pipes
// into category, title and optional M/D/YY date is split. Anything that
// doesn't fit is shown whole. With no Pseudo Title, the source title is used.
export function resolveVideoHeader(video = {}, sourceTitle = '') {
  const pseudoTitle = trimmed(video?.pseudoTitle);
  const fallbackTitle = pseudoTitle || trimmed(sourceTitle);
  const category = trimmed(video?.category);
  const publishedDate = trimmed(video?.publishedDate);
  if (category || publishedDate)
    return { category, title: fallbackTitle, date: formatVideoDate(publishedDate) };
  const parts = pseudoTitle.split('|').map((part) => part.trim());
  const date = parts.length === 3 ? formatVideoDate(parts[2]) : '';
  const fits = parts[0] && parts[1];
  if (fits && (parts.length === 2 || date)) return { category: parts[0], title: parts[1], date };
  return { category: '', title: fallbackTitle, date: '' };
}

export function videoCategoryTone(category) {
  const key = trimmed(category).toLowerCase();
  return key === 'news' || key === 'polls' ? key : '';
}
